// gatePassPrintService.ts
// Pure HTML/CSS-building + print-window logic for Gate Passes.
// No React here — this is reused by both the actual print job and the
// live preview iframe in GatePassGenerator.tsx, so the preview is always
// pixel-identical to what actually gets printed.

export interface StudentData {
  id: number;
  user_id: number;
  name: string;
  email_id: string;
  class_id: string;
  class_name: string;
  section: string | null;
  roll_no: number | string;
  father_name: string;
  father_no: string;
  profile_picture_url: string | null;
}

export interface GatePassSettings {
  schoolName: string;
  logoUrl: string | null;
  orientation: 'portrait' | 'landscape';
  examTitle: string;
  sessionLabel: string;
  examString: string;
  websiteUrl: string;
  watermarkText: string;
  watermarkImageUrl: string;
  watermarkWidth: number;
  watermarkRepeat: boolean;
  watermarkAngle: number;
  signatureDataUrl: string;
  signatureWidth: number;
}

export const CARDS_PER_PAGE = 3;

/** Full stylesheet for a gate pass card. Shared by print + preview. */
export const getGatePassStyles = (
  isLandscape: boolean,
  wmWidth: number,
  sigWidth: number,
  wmAngle: number = 0,
): string => `
  @page { size: A4 ${isLandscape ? 'landscape' : 'portrait'}; margin: 6mm; }

  @media print {
    body { margin: 0; padding: 0; }
    .no-print { display: none !important; }
    .page-wrapper {
      page-break-after: always;
      page-break-inside: avoid;
    }
    .page-wrapper:last-child { page-break-after: auto; }
    .cut-line { display: flex !important; }
  }

  * { box-sizing: border-box; }

  body {
    font-family: 'Times New Roman', Times, serif;
    margin: 0;
    padding: 8px;
    background: white;
    color: #111;
  }

  .controls {
    margin-bottom: 12px;
    text-align: center;
  }

  .controls button {
    background: #1e40af;
    color: white;
    border: none;
    padding: 7px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    margin: 0 5px;
  }

  .controls button.close-btn { background: #dc2626; }

  .page-wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
    width: 100%;
    margin-bottom: 4mm;
    ${!isLandscape ? 'padding: 0 18mm;' : ''}
  }

  /* Cut line between cards */
  .cut-line {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 2mm 0;
    position: relative;
  }

  .cut-line::before,
  .cut-line::after {
    content: '';
    flex: 1;
    border-top: 1.5px dashed #94a3b8;
  }

  .cut-icon {
    font-size: 14px;
    color: #94a3b8;
    line-height: 1;
    transform: rotate(180deg);
    display: inline-block;
    flex-shrink: 0;
  }

  .gatepass-page {
    border: 2px double #1e40af;
    border-radius: 3px;
    background: white;
    overflow: hidden;
    break-inside: avoid;
    position: relative;
  }

  /* Text watermark */
  .gp-watermark-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(${wmAngle}deg);
    font-size: ${isLandscape ? '54px' : '60px'};
    font-weight: 900;
    color: #1e40af;
    opacity: 0.05;
    pointer-events: none;
    z-index: 0;
    white-space: normal;
    word-break: break-word;
    text-align: center;
    width: ${wmWidth}%;
    letter-spacing: 6px;
    user-select: none;
    font-family: 'Times New Roman', Times, serif;
    line-height: 1.2;
  }

  /* Image watermark */
  .gp-watermark-img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(${wmAngle}deg);
    width: ${wmWidth}%;
    opacity: 0.07;
    pointer-events: none;
    z-index: 0;
    object-fit: contain;
    user-select: none;
  }

  /* Repeating watermark pattern — oversized + rotated as one layer so
     corners stay covered at any angle, then clipped by .gatepass-page. */
  .gp-watermark-repeat-wrap {
    position: absolute;
    inset: -75%;
    overflow: hidden;
    pointer-events: none;
    user-select: none;
    z-index: 0;
  }

  .gp-watermark-repeat-inner {
    position: absolute;
    inset: 0;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    justify-content: flex-start;
    transform: rotate(${wmAngle}deg);
    transform-origin: center center;
  }

  .gp-watermark-repeat-tile {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    box-sizing: border-box;
  }

  .gp-watermark-repeat-text {
    font-family: 'Times New Roman', Times, serif;
    font-weight: 900;
    font-size: ${isLandscape ? '19px' : '21px'};
    color: #1e40af;
    opacity: 0.06;
    letter-spacing: 2px;
    white-space: nowrap;
  }

  .gp-watermark-repeat-img {
    width: 56px;
    opacity: 0.08;
    object-fit: contain;
  }

  .gp-header,
  .gp-exam-string,
  .gp-body,
  .gp-signatures,
  .gp-footer {
    position: relative;
    z-index: 1;
  }

  .gp-header {
    border-bottom: 1.5px solid #1e40af;
    padding: ${isLandscape ? '6px 9px 5px' : '8px 14px 6px'};
    display: flex;
    align-items: center;
    gap: ${isLandscape ? '8px' : '10px'};
  }

  .gp-header-logo {
    width: ${isLandscape ? '57px' : '68px'};
    height: ${isLandscape ? '57px' : '68px'};
    object-fit: contain;
    flex-shrink: 0;
    border-radius: 4px;
  }

  .gp-header-logo-placeholder {
    width: ${isLandscape ? '57px' : '68px'};
    height: ${isLandscape ? '57px' : '68px'};
    flex-shrink: 0;
    background: #1e40af;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: ${isLandscape ? '15px' : '19px'};
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .gp-header-text {
    flex: 1;
    text-align: center;
  }

  .gp-school-name {
    font-size: ${isLandscape ? '18px' : '20.4px'};
    font-weight: bold;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #1e40af;
    margin: 0 0 2px 0;
  }

  .gp-subtitle {
    font-size: ${isLandscape ? '14.4px' : '15.6px'};
    font-weight: 600;
    color: #374151;
    margin: 0 0 2px 0;
    letter-spacing: 0.3px;
  }

  .gp-subtitle-gatepass {
    color: #1e40af;
    font-weight: 800;
  }

  .gp-badge {
    display: inline-block;
    margin-top: ${isLandscape ? '3px' : '5px'};
    padding: 2px 12px;
    border: 1px solid #1e40af;
    border-radius: 10px;
    font-size: ${isLandscape ? '13.2px' : '14.4px'};
    font-weight: 700;
    color: #1e40af;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .gp-exam-string {
    background: #f0f4ff;
    border-bottom: 1px solid #c7d7f9;
    padding: ${isLandscape ? '5px 12px' : '5px 14px'};
    font-size: ${isLandscape ? '13.2px' : '14.4px'};
    color: #1e3a8a;
    font-style: italic;
    text-align: center;
    white-space: pre-line;
    line-height: 1.3;
  }

  .gp-body {
    padding: ${isLandscape ? '7px 9px' : '10px 14px'};
    display: flex;
    gap: ${isLandscape ? '9px' : '14px'};
    align-items: flex-start;
  }

  .gp-photo-col {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${isLandscape ? '5px' : '7px'};
  }

  .gp-photo-wrap {
    width: ${isLandscape ? '117px' : '123px'};
    height: ${isLandscape ? '140px' : '146px'};
    border: 1.5px solid #1e40af;
    border-radius: 3px;
    overflow: hidden;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .gp-photo-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .gp-photo-placeholder {
    font-size: ${isLandscape ? '47px' : '54px'};
    font-weight: bold;
    color: #1e40af;
  }

  .gp-student-sig {
    width: ${isLandscape ? '117px' : '123px'};
    text-align: center;
  }

  .gp-sig-line {
    border-top: 1px solid #374151;
    margin-bottom: 2px;
  }

  .gp-sig-label {
    font-size: 12px;
    color: #374151;
    letter-spacing: 0.2px;
  }

  .gp-details-col {
    flex: 1;
    min-width: 0;
  }

  .gp-details-table {
    width: 100%;
    border-collapse: collapse;
  }

  .gp-details-table tr td {
    padding: ${isLandscape ? '3.5px 5px' : '4px 5px'};
    font-size: ${isLandscape ? '14.4px' : '15.6px'};
    font-weight: 600;
    border-bottom: 0.5px solid #e5e7eb;
    vertical-align: top;
    line-height: 1.3;
  }

  .gp-details-table tr:last-child td { border-bottom: none; }

  .gp-details-table .lbl {
    font-weight: 800;
    color: #1e40af;
    width: ${isLandscape ? '96px' : '108px'};
    white-space: nowrap;
  }

  .gp-details-table .val { color: #000000; font-weight: 600; }

  .gp-signatures {
    display: flex;
    justify-content: flex-end;
    padding: ${isLandscape ? '6px 9px 7px' : '8px 14px 10px'};
    border-top: 1px solid #e5e7eb;
    margin-top: 2px;
  }

  .gp-auth-sig {
    text-align: center;
    width: ${isLandscape ? '115px' : '110px'};
  }

  .gp-auth-sig-img {
    width: ${sigWidth}%;
    max-height: ${isLandscape ? '32px' : '28px'};
    object-fit: contain;
    display: block;
    margin: 0 auto 2px auto;
  }

  .gp-auth-space {
    height: 0;
  }

  .gp-auth-line {
    border-top: 1px solid #1a1a1a;
    margin-bottom: 2px;
  }

  .gp-auth-label {
    font-size: 13.2px;
    color: #1a1a1a;
    font-weight: 700;
    letter-spacing: 0.2px;
  }

  .gp-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 3px 10px;
    font-size: 10.8px;
    color: #6b7280;
    border-top: 0.5px solid #e5e7eb;
    letter-spacing: 0.05em;
  }

  .gp-footer-left {
    flex: 1;
    text-align: left;
  }

  .gp-footer-center {
    flex: 1;
    text-align: center;
    font-weight: 700;
    color: #1e40af;
    font-style: italic;
    font-size: 11.5px;
  }

  .gp-footer-right {
    flex: 1;
    text-align: right;
  }

  .gp-footer-website {
    color: #1e40af;
    font-weight: 600;
  }

  /* Preview-only wrapper (harmless outside the iframe / print window) */
  .gp-preview-body {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 12px;
    background: #eef2f9;
  }
`;

/** Escapes & applies <br> support for watermark text, shared by both modes. */
const formatWatermarkText = (text: string): string => {
  const safe = text.trim().replace(/&/g, '&amp;').replace(/</g, '<').replace(/>/g, '>');
  return safe.replace(/&lt;br\s*\/?&gt;/gi, '<br/>');
};

/**
 * Builds a full-bleed grid of repeated watermark tiles, covering the card
 * both horizontally and vertically (not just across, or just down).
 * Tile width (and therefore how dense the repeat looks) is driven by
 * watermarkWidth, same slider used for the single watermark's size.
 */
const buildRepeatedWatermarkHtml = (settings: GatePassSettings): string => {
  const isImage = Boolean(settings.watermarkImageUrl.trim());
  if (!isImage && !settings.watermarkText.trim()) return '';

  // Wrap is oversized to 250% (100% + 2 * 75% inset) so it stays covered when rotated.
  const tileWidthPercent = Math.max(15, Math.min(settings.watermarkWidth, 70));
  const cols = Math.max(3, Math.ceil(250 / tileWidthPercent));
  // Tiles are much wider than tall, so a plain cols x cols grid leaves gaps
  // top/bottom on the oversized wrap — use a generous fixed row count instead
  // so the pattern fully repeats vertically too, regardless of card height.
  const rows = 18;
  const totalTiles = Math.min(220, cols * rows);

  const tileContent = isImage
    ? `<img src="${settings.watermarkImageUrl.trim()}" class="gp-watermark-repeat-img" alt="watermark" />`
    : `<span class="gp-watermark-repeat-text">${formatWatermarkText(settings.watermarkText)}</span>`;

  const tiles = Array.from({ length: totalTiles })
    .map(() => `<div class="gp-watermark-repeat-tile" style="flex-basis:${tileWidthPercent}%;">${tileContent}</div>`)
    .join('');

  return `
    <div class="gp-watermark-repeat-wrap">
      <div class="gp-watermark-repeat-inner">${tiles}</div>
    </div>
  `;
};

/** Builds the markup for a single gate pass card. */
export const buildGatePassCardHtml = (
  student: StudentData,
  className: string,
  settings: GatePassSettings,
): string => {
  const isLandscape = settings.orientation === 'landscape';

  const photoHtml = student.profile_picture_url
    ? `<img src="${student.profile_picture_url}" alt="${student.name}" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />
       <div class="gp-photo-placeholder" style="display:none;">${student.name.charAt(0)}</div>`
    : `<div class="gp-photo-placeholder">${student.name.charAt(0)}</div>`;

  const examStringHtml = settings.examString.trim()
    ? `<div class="gp-exam-string">${settings.examString.trim()}</div>`
    : '';

  const logoHtml = settings.logoUrl
    ? `<img src="${settings.logoUrl}" class="gp-header-logo" alt="logo" />`
    : `<div class="gp-header-logo-placeholder">SP</div>`;

  let watermarkHtml = '';
  if (settings.watermarkRepeat) {
    watermarkHtml = buildRepeatedWatermarkHtml(settings);
  } else if (settings.watermarkImageUrl.trim()) {
    watermarkHtml = `<img src="${settings.watermarkImageUrl.trim()}" class="gp-watermark-img" alt="watermark" />`;
  } else if (settings.watermarkText.trim()) {
    watermarkHtml = `<div class="gp-watermark-text">${formatWatermarkText(settings.watermarkText)}</div>`;
  }

  const footerLeft = settings.websiteUrl.trim()
    ? `<span class="gp-footer-website">${settings.websiteUrl.trim()}</span>`
    : `<span></span>`;

  const sigHtml = settings.signatureDataUrl
    ? `<img src="${settings.signatureDataUrl}" class="gp-auth-sig-img" alt="signature" />`
    : `<div class="gp-auth-space"></div>`;

  return `
    <div class="gatepass-page">
      ${watermarkHtml}
      <div class="gp-header">
        ${logoHtml}
        <div class="gp-header-text">
          <p class="gp-school-name">${settings.schoolName}</p>
          <p class="gp-subtitle">
            <span class="gp-subtitle-gatepass">Gate Pass</span> — Session ${settings.sessionLabel}
          </p>
          ${settings.examTitle.trim() ? `<span class="gp-badge">${settings.examTitle.trim()}</span>` : ''}
        </div>
      </div>

      ${examStringHtml}

      <div class="gp-body">
        <div class="gp-photo-col">
          <div class="gp-photo-wrap">${photoHtml}</div>
        </div>

        <div class="gp-details-col">
          <table class="gp-details-table">
            <tr>
              <td class="lbl">Name</td>
              <td class="val">: ${student.name}</td>
            </tr>
            <tr>
              <td class="lbl">Parentage</td>
              <td class="val">: ${student.father_name}</td>
            </tr>
            <tr>
              <td class="lbl">Class</td>
              <td class="val">: ${className}</td>
            </tr>
            ${student.section ? `
            <tr>
              <td class="lbl">Section</td>
              <td class="val">: ${student.section}</td>
            </tr>` : ''}
            <tr>
              <td class="lbl">Roll No.</td>
              <td class="val">: ${student.roll_no}</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="gp-signatures">
        <div class="gp-auth-sig">
          ${sigHtml}
          <div class="gp-auth-line"></div>
          <div class="gp-auth-label">Coordinator</div>
        </div>
      </div>

      <div class="gp-footer">
        <div class="gp-footer-left">${footerLeft}</div>
        <div class="gp-footer-center">Best of Luck</div>
        <div class="gp-footer-right">Powered by SkoolPillar</div>
      </div>
    </div>
  `;
};

/** Full standalone HTML document for the print window (controls + all pages). */
export const buildPrintDocument = (
  students: StudentData[],
  className: string,
  settings: GatePassSettings,
): string => {
  const isLandscape = settings.orientation === 'landscape';
  const sectionText = ''; // kept for parity with original signature; unused directly in markup
  const title = `Gate Pass - Class ${className}`;

  const cutLineHtml = `
    <div class="cut-line">
      <span class="cut-icon">✂</span>
    </div>
  `;

  const allCards = students.map(s => buildGatePassCardHtml(s, className, settings));

  const pages: string[] = [];
  for (let i = 0; i < allCards.length; i += CARDS_PER_PAGE) {
    const chunk = allCards.slice(i, i + CARDS_PER_PAGE);
    const cardsWithCutLines = chunk
      .map((card, idx) => (idx < chunk.length - 1 ? card + cutLineHtml : card))
      .join('');
    pages.push(`<div class="page-wrapper">${cardsWithCutLines}</div>`);
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>${getGatePassStyles(isLandscape, settings.watermarkWidth, settings.signatureWidth, settings.watermarkAngle)}</style>
      </head>
      <body>
        <div class="no-print controls">
          <button onclick="window.print()">🖨️ Print / Save as PDF</button>
          <button class="close-btn" onclick="window.close()">❌ Close</button>
        </div>
        ${pages.join('')}
      </body>
    </html>
  `;
};

/** Standalone HTML document showing a single card — used for the live preview iframe. */
export const buildPreviewDocument = (
  student: StudentData,
  className: string,
  settings: GatePassSettings,
): string => {
  const isLandscape = settings.orientation === 'landscape';
  const cardHtml = buildGatePassCardHtml(student, className, settings);

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <style>${getGatePassStyles(isLandscape, settings.watermarkWidth, settings.signatureWidth, settings.watermarkAngle)}</style>
      </head>
      <body class="gp-preview-body">
        ${cardHtml}
      </body>
    </html>
  `;
};

/**
 * Opens a new window, writes the multi-page print document, and triggers print.
 * Returns true if the window opened successfully.
 */
export const printGatePasses = (
  students: StudentData[],
  className: string,
  settings: GatePassSettings,
): boolean => {
  const html = buildPrintDocument(students, className, settings);
  const win = window.open('', '_blank');
  if (!win) return false;

  win.document.write(html);
  win.document.close();
  setTimeout(() => {
    win.focus();
    win.print();
  }, 500);
  return true;
};